import React, { FC, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import translate from '@translate';
import { checkFormValidity } from 'src/utils/formFunctions';
import TextField from 'src/components/TextField';
import Button from 'src/components/Button';
import { sizes } from 'src/themes/default';
import { FormErrorsType } from '../../types/global';
import { ProductEanType } from 'src/types/product';

type NewEanBlockPropsType = {
    data?: ProductEanType[];
    onChange: (value: ProductEanType[]) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
};

const INPUT_NAME = 'NEW_EAN';

const NewEanBlock: FC<NewEanBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [ean, setEan] = useState<string>('');
    const [eanError, setEanError] = useState<boolean>(false);
    const [duplicate, setDuplicate] = useState<boolean>(false);

    const changeValue = (value: string, error: boolean) => {
        setEan(value);
        setEanError(error);
        setDuplicate(false);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (Object.keys(newFormErrors).length) {
            setEanError(true);
            return;
        }

        const newEan = ean.trim();
        if (!newEan || newEan.includes(',')) {
            setEanError(true);
            return;
        }
        if ((props.data || []).some((item) => item.ean === newEan)) {
            setEanError(true);
            setDuplicate(true);
            return;
        }

        const existingIds = (props.data || []).map((item) => item.ID || 0);
        const newId = Math.min(0, ...existingIds) - 1;

        props.onChange([...(props.data || []), { ID: newId, ean: newEan, active: true }]);

        setEan('');
        setEanError(false);
        setDuplicate(false);
    };

    return (
        <form ref={formRef} style={{ display: 'contents' }}>
            <Box sx={{ flex: '0 0 25%', maxWidth: '25%', minWidth: 0 }}>
                <TextField
                    value={ean}
                    error={eanError || !!props.editErrors.EANS}
                    onChange={(value, error) => changeValue(String(value ?? ''), error)}
                    name={INPUT_NAME}
                    label={translate('productDetail.eansTab.addEan')}
                    errorHelperText={duplicate ? translate('productDetail.eansTab.duplicateEan') : undefined}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                    inputProps={{ 'data-testid': 'product-eans-input' }}
                />
            </Box>
            <Button
                onClick={handleAddRow}
                disabled={props.disableEdit}
                sx={{ flexShrink: 0, marginTop: sizes.input_top_PD, whiteSpace: 'nowrap' }}
                data-testid="product-eans-add-btn"
            >
                {translate('productDetail.eansTab.add')}
            </Button>
        </form>
    );
};

export default NewEanBlock;
